<template>
    <div>
        <button
            type="button"
            @click="open = !open"
            class="border mt-1 block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md mt-1 w-full"
        >
            {{ group ? group.text : $t('messages.select') }}
        </button>
        <SlideOver :title="$tc('messages.groups', 2)" :show="open" @close="open = false">
            <div>
                <details class="mt-3 sm:col-span-12" :open="openForm">
                    <summary>
                        <PlusButton @click="openForm = !openForm" />
                    </summary>

                    <div class="p-2 border rounded-lg">
                        <form :action="endpoint" method="post" @submit.prevent="submit">
                            <div class="grid grid-cols-2 gap-2 md:gap-6">
                                <div class="">
                                    <div class="mt-1">
                                        <FormLabel :value="$t('messages.grade_level')" for="grade_level_id" />
                                        <FormSelect
                                            property-value="id"
                                            property-name="label"
                                            id="grade_level_id"
                                            required
                                            v-model="form.grade_level_id"
                                            :options="gradeLevels"
                                            class="mt-1 w-full"
                                        />
                                        <FormError :value="form.errors.grade_level_id" class="mt-2" />
                                    </div>
                                </div>

                                <div class="">
                                    <div class="mt-1">
                                        <FormLabel :value="$t('messages.letter')" for="letter" />
                                        <FormSelect
                                            id="letter"
                                            v-model="form.letter"
                                            required
                                            :options="letters"
                                            class="mt-1 w-full"
                                        />
                                        <FormError :value="form.errors.letter" class="mt-2" />
                                    </div>
                                </div>

                                <div class="col-span-2">
                                    <div class="mt-1">
                                        <FormLabel :value="$t('messages.name')" for="name" />
                                        <FormInput
                                            id="name"
                                            v-model="form.name"
                                            name="name"
                                            :placeholder="$t('messages.(optional)')"
                                            type="text"
                                            max="191"
                                            class="mt-1 w-full"
                                        />
                                        <FormError :value="form.errors.name" class="mt-2" />
                                    </div>
                                </div>
                            </div>

                            <div class="flex justify-end mt-5">
                                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
                            </div>
                        </form>
                    </div>
                </details>
            </div>

            <div class="mt-5">
                {{ $t('messages.select') }}
            </div>

            <fieldset v-if="gradeLevelsWithGroups.length">
                <legend class="sr-only">
                    {{ $tc('messages.groups', 2) }}
                </legend>
                <div class="space-y-4">
                    <div v-for="(gradeLevel, index) in gradeLevelsWithGroups" :key="gradeLevel.id">
                        <hr v-if="index !== 0" class="mt-3 mb-2" />

                        <div class="flex gap-x-4">
                            <div>
                                {{ gradeLevel.label }}
                            </div>
                            <div class="flex gap-x-2 flex-wrap">
                                <div v-for="group in gradeLevel.options" :key="group.value">
                                    <label
                                        :class="[
                                            group.value == selected
                                                ? 'border-transparent ring-2 ring-indigo-500'
                                                : 'border-gray-300',
                                        ]"
                                        class="relative block bg-white border rounded-lg shadow-sm px-2 py-1 cursor-pointer sm:justify-between focus:outline-none"
                                    >
                                        <input
                                            type="radio"
                                            name="group"
                                            v-model="selected"
                                            :value="group.value"
                                            class="sr-only"
                                            aria-labelledby="server-size-1-label"
                                            aria-describedby="server-size-1-description-0 server-size-1-description-1"
                                        />
                                        <div>
                                            {{ group.text }}
                                        </div>
                                        <div
                                            :class="[
                                                group.value == selected
                                                    ? 'border border-indigo-500'
                                                    : 'border-2 border-transparent',
                                            ]"
                                            class="absolute -inset-px rounded-lg border-2 pointer-events-none"
                                            aria-hidden="true"
                                        ></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </SlideOver>
    </div>
</template>

<script>
import SlideOver from '@/components/SlideOver.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormError from '@/components/Forms/FormError.vue';
import PlusButton from '@/components/Forms/PlusButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';

export default {
    name: 'StudentGroupSelectorPanel',
    components: {
        FormSelect,
        SubmitFormButton,
        PlusButton,
        SlideOver,
        FormLabel,
        FormInput,
        FormError,
    },
    props: {
        modelValue: {
            required: true,
        },
        endpoint: {
            required: true,
            type: String,
        },
        gradeLevels: {
            required: true,
            type: Array,
        },
        letters: {
            required: true,
            type: Array,
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            open: false,
            openForm: !this.gradeLevels.length,
            selected: this.modelValue,
            form: this.$inertia.form({
                name: '',
                letter: '',
                grade_level_id: null,
            }),
        };
    },
    watch: {
        selected(newVal) {
            this.$emit('update:modelValue', newVal);
            this.open = false;
        },
    },
    computed: {
        group() {
            const group = this.gradeLevels
                .filter((gl) => gl.options.length > 0)
                .flatMap((gl) => gl.options)
                .find((e) => e.value == this.selected);

            return group ? group : null;
        },
        gradeLevelsWithGroups() {
            return this.gradeLevels.filter((gl) => gl.options.length > 0);
        },
    },
    methods: {
        submit() {
            this.form.post(this.endpoint, {
                onSuccess: () => {
                    this.form.reset('name', 'letter', 'grade_level_id');
                    this.openForm = false;
                },
            });
        },
    },
    mounted() {
        this.openForm = this.gradeLevelsWithGroups.length === 0;
    },
};
</script>

<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
